import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Warehouse, LogIn, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { homeFor } from '@/App';
import { toast } from 'sonner';

// Quick-fill helpers ONLY for Kasir and Admin (Owner/Salesman removed intentionally).
// SECURITY: only the username hint is stored on the client. Password harus diketik
// manual oleh user — tidak boleh disimpan di bundle frontend (mencegah kebocoran
// kredensial bila source dilihat via DevTools).
const QUICK_FILL = [
  { username: 'admin', role: 'Admin' },
  { username: 'kasir', role: 'Kasir' },
];

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(homeFor(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Selamat datang, ${u.full_name}`);
      navigate(homeFor(u.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login gagal');
    } finally { setSubmitting(false); }
  };

  // Isi hanya username (password TIDAK disimpan di client demi keamanan).
  const fillDemo = (u) => { setUsername(u.username); setPassword(''); };

  return (
    <div className="min-h-screen w-full flex items-stretch">
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground">
              <Warehouse className="h-6 w-6" />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold">ERP Zainusa Pos</div>
              <div className="text-xs text-muted-foreground">Sistem Pos Mandiri Zainusa Putra Manunggal</div>
            </div>
          </div>

          <Card className="border shadow-sm">
            <CardContent className="p-6">
              <h1 className="font-heading text-2xl font-semibold">Masuk ke Akun</h1>
              <p className="text-sm text-muted-foreground mt-1">Gunakan username & password yang diberikan Owner.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="mis. owner" required autoFocus />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" data-testid="login-password-input" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1 rounded hover:bg-accent" aria-label="toggle password">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" className="w-full h-11" disabled={submitting}>
                  {submitting ? 'Memproses...' : (<><LogIn className="h-4 w-4 mr-2" />Masuk</>)}
                </Button>
              </form>

              <div className="mt-6 border-t pt-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Isi cepat:</span>
                  {QUICK_FILL.map((d) => (
                    <button
                      key={d.username}
                      type="button"
                      onClick={() => fillDemo(d)}
                      data-testid={`quickfill-${d.username}`}
                      className="text-xs rounded-full border px-3 py-1 hover:bg-accent transition-colors font-medium"
                    >
                      {d.role}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center text-xs text-muted-foreground" data-testid="login-credit">
            Di bangun dan di kembangkan langsung oleh <span className="font-medium text-foreground">Daiva Dante S</span>
          </div>
        </div>
      </div>

      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=80"
          alt="Warehouse"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="font-heading text-3xl font-semibold drop-shadow">Kelola gudang, penjualan & tim sales dalam satu aplikasi.</div>
          <div className="mt-2 text-sm opacity-90">POS real-time, kontrol HPP untuk Owner, dan dasbor mobile untuk sales.</div>
        </div>
      </div>
    </div>
  );
}
